// highlight text in textarea - inspired by https://codersblock.com/blog/highlight-text-inside-a-textarea/
import { Box, TextField, TextFieldProps } from "@mui/material"
import { KeyboardEventHandler, useEffect, useRef } from "react"
import DOMPurify from "dompurify"
import { Control, Controller, FieldValues, Path } from "react-hook-form"
import { getTextWithMarkedAreas } from "@components/HighlightTextArea/helpers"
import useHTMLElementSize from "@hooks/useHTMLElementSize"

export type HighlightArea = { startIndex: number; endIndex: number }

type HighlightTextAreaProps<TFieldValues extends FieldValues> = TextFieldProps & {
  control: Control<TFieldValues>
  name: Path<TFieldValues>
  highlightedAreas: HighlightArea[]
}

const HighlightTextArea = <TFieldValues extends FieldValues>({
  control,
  name,
  highlightedAreas,
  ...textFieldProps
}: HighlightTextAreaProps<TFieldValues>) => {
  const highlightAreaRef = useRef<HTMLDivElement>()

  // MUI under the hood uses 2 textareas for different things
  const visibleTextAreaRef = useRef<HTMLTextAreaElement>()
  const hiddenTextAreaRef = useRef<HTMLTextAreaElement>()

  const { height: visibleTextAreaHeight } = useHTMLElementSize(visibleTextAreaRef.current)

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    // prevent switching to other field on TAB, instead add two space characters
    if (event.key === "Tab") {
      event.preventDefault()
      const textArea = event.target as HTMLTextAreaElement
      textArea.setRangeText("  ", textArea.selectionStart, textArea.selectionStart, "end")
    }
  }

  useEffect(() => {
    syncScrollAreas()
  }, [highlightedAreas])

  const syncScrollAreas = () => {
    if (!highlightAreaRef.current || !hiddenTextAreaRef.current) return

    highlightAreaRef.current.scrollTop = hiddenTextAreaRef.current.scrollTop
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            position: "relative",
            border: "1px solid black",
            boxSizing: "border-box",
          }}
        >
          {highlightedAreas.length > 0 && (
            <Box
              ref={highlightAreaRef}
              sx={{
                width: "100%",
                height: "100%",
                position: "absolute",
                zIndex: 1,
                overflow: "auto",
                mark: { color: "transparent", backgroundColor: "#ADD8E6" },
              }}
            >
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  wordWrap: "break-word",
                  color: "transparent",
                  lineHeight: "22px",
                  fontSize: "22px",
                  fontFamily: "monospace",
                  letterSpacing: 0,
                  width: "100%",
                  height: `${visibleTextAreaHeight}px`,
                }}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(getTextWithMarkedAreas(field.value, highlightedAreas)),
                }}
              />
            </Box>
          )}

          <TextField
            multiline
            sx={{
              display: "flex",
              flexGrow: 1,
              height: "100%",
              border: "1px solid black",
              ".MuiFormHelperText-root": {
                whiteSpace: "pre-wrap",
                wordWrap: "break-word",
              },
              " & .MuiOutlinedInput-root fieldset": {
                border: "none",
              },
              ...textFieldProps.sx,
            }}
            inputProps={{ ref: visibleTextAreaRef }}
            InputProps={{
              ref: hiddenTextAreaRef,
              style: {
                letterSpacing: 0,
                lineHeight: "22px",
                fontFamily: "monospace",
                fontSize: "22px",
                overflow: "auto",
                position: "absolute",
                zIndex: 2,
                width: "100%",
                ...textFieldProps.InputProps?.style,
              },
              sx: {
                height: "100%",
                alignItems: "flex-start",
                p: 0,
                ...textFieldProps.InputProps?.sx,
              },
              onKeyDown: handleKeyDown,
              onScroll: syncScrollAreas,
              ...textFieldProps.InputProps,
            }}
            {...textFieldProps}
            {...field}
          />
        </Box>
      )}
    />
  )
}

export default HighlightTextArea
